import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaEye, 
  FaSearch, 
  FaFilter, 
  FaExclamationTriangle,
  FaClock,
  FaCheckCircle 
} from 'react-icons/fa';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import './Disputes.css';
import Pagination from '../Common/Pagination';

const DisputesList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });
  const [stats, setStats] = useState({
    totalDisputes: 0,
    openDisputes: 0,
    resolvedDisputes: 0,
    avgResolutionTime: 0
  });

  const fetchDisputes = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        status: statusFilter === 'all' ? undefined : statusFilter.toUpperCase(),
        priority: priorityFilter === 'all' ? undefined : priorityFilter.toUpperCase()
      };
      const [disputesRes, statsRes] = await Promise.all([
        api.get(ENDPOINTS.DISPUTES.base, { params }),
        api.get(ENDPOINTS.DISPUTES.stats)
      ]);

      const disputesData = disputesRes.data || [];
      const statsData = statsRes.data || statsRes;

      setDisputes(disputesData.data || disputesData);
      setPagination(prev => ({ ...prev, total: disputesData.meta?.total || disputesRes.total || 0 }));
      setStats({
        totalDisputes: statsData.totalDisputes || 0,
        openDisputes: statsData.openDisputes || 0,
        resolvedDisputes: statsData.resolvedDisputes || 0,
        avgResolutionTime: statsData.avgResolutionTime || 0
      });
    } catch (error) {
      console.error('Failed to fetch disputes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDisputes();
  }, [pagination.page, statusFilter, priorityFilter]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'OPEN': { class: 'badge-danger', icon: <FaExclamationTriangle />, label: 'Open' },
      'IN_REVIEW': { class: 'badge-warning', icon: <FaClock />, label: 'In Review' },
      'RESOLVED': { class: 'badge-success', icon: <FaCheckCircle />, label: 'Resolved' },
      'REJECTED': { class: 'badge-secondary', icon: <FaCheckCircle />, label: 'Rejected' }
    };
    const config = statusConfig[status] || statusConfig.OPEN;
    return (
      <span className={`badge ${config.class}`}>
        {config.icon} {config.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      'HIGH': { class: 'priority-high', label: 'High' },
      'MEDIUM': { class: 'priority-medium', label: 'Medium' },
      'LOW': { class: 'priority-low', label: 'Low' }
    };
    const config = priorityConfig[priority] || priorityConfig.MEDIUM;
    return <span className={`priority-badge ${config.class}`}>{config.label}</span>;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Fetching disputes...</p>
      </div>
    );
  }

  return (
    <div className="disputes-page">
      <div className="page-header">
        <div>
          <h1>Disputes Management</h1>
          <p className="page-subtitle">Handle customer and provider disputes</p>
        </div>
      </div>

      {/* Filters */}
      <div className="content-card filters-card">
        <div className="filters-row">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search disputes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-group">
            <FaFilter className="filter-icon" />
            <select 
              value={statusFilter} 
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="in_review">In Review</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>

          <div className="filter-group">
            <select 
              value={priorityFilter} 
              onChange={(e) => {
                setPriorityFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="filter-select"
            >
              <option value="all">All Priority</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* Disputes Table */}
      <div className="content-card">
        <div className="card-header">
          <h3>All Disputes ({pagination.total})</h3>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle custom-table">
            <thead>
              <tr>
                <th>Booking ID</th>
                <th>Filed By</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {disputes.map((dispute) => (
                <tr key={dispute.id}>
                  <td>
                    <span className="booking-id">BID{dispute.booking?.id || 'N/A'}</span>
                  </td>
                  <td>{dispute.customer?.firstName} {dispute.customer?.lastName}</td>
                  <td>{getPriorityBadge(dispute.priority)}</td>
                  <td>{getStatusBadge(dispute.status)}</td>
                  <td>
                    <span className="date-text">
                      {new Date(dispute.createdAt).toLocaleDateString()}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Link 
                        to={`/disputes/${dispute.id}`}
                        className="action-btn view"
                        title="View Details"
                      >
                        <FaEye />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <Pagination 
          currentPage={pagination.page}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        />
      </div>
    </div>
  );
};

export default DisputesList;